import React from "react";

import { FaX, FaCheck } from "react-icons/fa6";

import { formatDate } from "../../../utils/datetime";
import { getUserDisplayName } from "../../../utils/business";

/**
 * Badge showing the current dispatch lifecycle stage for one day (Draft,
 * Dispatched, Reviewed, or Modified). Hover reveals who changed the stage and when.
 *
 * @param {Object} props
 * @param {string} [props.className] Additional classes for the badge.
 * @param {Object} props.dayDetails Day row from dispatch day details (`dispatch_stage` history).
 * @param {boolean} [props.isLastDay] When true, positions the hover detail panel to the left.
 * @returns {JSX.Element|null}
 */
const DispatchStage = ({ className, dayDetails, isLastDay }) => {
  if (!dayDetails) {
    return null;
  }

  const stageDetails = dayDetails["dispatch_stage"];
  const stage =
    stageDetails.length > 0
      ? stageDetails[stageDetails.length - 1].stage
      : "Draft";

  const colors = {
    Draft: "bg-gray-300",
    Dispatched: "bg-blue-600 hover:bg-blue-800 text-white",
    Reviewed: "bg-green-700 hover:bg-green-900 text-white",
    Modified: "bg-yellow-500 hover:bg-yellow-700 text-white",
  };

  const textColors = {
    Draft: "text-gray-800",
    Dispatched: "text-blue-900",
    Reviewed: "text-green-900",
    Modified: "text-yellow-500",
  };

  const color = colors[stage];

  return (
    <div className="flex flex-row cursor-default">
      <div className={`${className} ${color} rounded-xl px-2 peer/state`}>
        {stage}
      </div>

      {stage !== "Draft" ? ( // Draft doesn't contain any dispatch stage details yet
        <div
          className={`fixed z-40 p-1 mt-[22px] ${isLastDay ? "-translate-x-[80%] origin-top-right" : "-translate-x-1/3 origin-top"} py-1 text-left rounded peer-hover/state:scale-100 hover:scale-100 delay-75 scale-0 transition-all ease-in-out bg-gray-300 shadow-lg shadow-gray-500 truncate`}
        >
          {stageDetails.map((stageDetail, index) => {
            const time = stageDetail.time.split(":").slice(0, 2).join(":");
            return (
              <div key={index}>
                <div
                  className={`px-2 ${index === stageDetails.length - 1 && stageDetails.length > 1 ? colors[stageDetail.stage] + " text-white rounded-3xl mx-0 mt-1" : textColors[stageDetail.stage]}`}
                >
                  {stageDetail.stage} {formatDate(stageDetail.date, "MM/DD/YY")}, {time} {stageDetail.user ? 
                  getUserDisplayName(stageDetail.user) : "RSApp Auto-Review"}
                </div>
                {stageDetail.stage === "Reviewed" && (
                  <ul className="text-black text-xs pr-2 pl-4">
                    <li className="flex flex-row items-center">
                      &#8226; All Accidents reported{" "}
                      {stageDetail["accidents_reported"] ? (
                        <FaCheck size={12} className="ml-2" />
                      ) : (
                        <FaX size={12} className="ml-2" />
                      )}
                    </li>
                    <li className="flex flex-row items-center">
                      &#8226; All Change orders issued{" "}
                      {stageDetail["change_orders_issued"] ? (
                        <FaCheck size={12} className="ml-2" />
                      ) : (
                        <FaX size={12} className="ml-2" />
                      )}
                    </li>
                    <li className="flex flex-row items-center">
                      &#8226; All T&M reported{" "}
                      {stageDetail["time_material_reported"] ? (
                        <FaCheck size={12} className="ml-2" />
                      ) : (
                        <FaX size={12} className="ml-2" />
                      )}
                    </li>
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default DispatchStage;
